/**
 * React Query hooks for Posts
 *
 * Provides data fetching, caching, and mutations for posts.
 * Designed for granular cache updates - SSE events update individual posts
 * without refetching the entire list.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { postsService } from "../services/postsService";
import { PostItem, ValidStatuses } from "@/core/Post/PostItem.model";
import { Post, PostStatus, UpdatePostRequest } from "@/core/types/postTypes";
import { postKeys, queryClient } from "@/core/queryClient";
import { UserRole } from "@/services/urlService";
import { useAppStore } from "@/core/store/useAppStore";

/**
 * Fetches all posts for a client
 */
export function usePostsQuery(clientId: string | undefined) {
  return useQuery({
    queryKey: clientId ? postKeys.all(clientId) : ["posts", "none"],
    queryFn: async () => {
      if (!clientId) return [];
      const posts = await postsService.getClientPostsWithFilters(clientId, {});
      return posts.map((p) => PostItem.fromLegacyPost(p, UserRole.ADMIN, clientId));
    },
    enabled: !!clientId,
  });
}

/**
 * Fetches a single post by ID
 */
export function useSinglePostQuery(clientId: string | undefined, postId: string | undefined) {
  return useQuery({
    queryKey: clientId && postId ? postKeys.single(clientId, postId) : ["posts", "none"],
    queryFn: async () => {
      if (!clientId || !postId) return null;
      const post = await postsService.getPost(clientId, postId);
      return PostItem.fromLegacyPost(post, UserRole.ADMIN, clientId);
    },
    enabled: !!clientId && !!postId,
  });
}

/**
 * Mutation for updating a post
 * Uses optimistic updates for instant UI feedback
 */
export function useUpdatePostMutation(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ postId, updates }: { postId: string; updates: UpdatePostRequest }) => {
      const response = await postsService.updatePost(clientId, postId, updates);
      return PostItem.fromLegacyPost(response.post, UserRole.ADMIN, clientId);
    },
    onMutate: async ({ postId, updates }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: postKeys.all(clientId) });

      // Snapshot the previous value
      const previousPosts = queryClient.getQueryData<PostItem[]>(postKeys.all(clientId));

      // Optimistically update the cache
      if (previousPosts) {
        queryClient.setQueryData<PostItem[]>(postKeys.all(clientId), (old) =>
          old?.map((post) =>
            post.id === postId ? post.clone(updates as Partial<Post>) : post
          )
        );
      }

      return { previousPosts };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousPosts) {
        queryClient.setQueryData(postKeys.all(clientId), context.previousPosts);
      }
    },
    onSettled: (_data, _error, { postId }) => {
      // Invalidate the single post to refetch latest
      queryClient.invalidateQueries({ queryKey: postKeys.single(clientId, postId) });
    },
  });
}

/**
 * Mutation for updating post status
 * Uses optimistic updates for instant UI feedback
 */
export function useUpdatePostStatusMutation(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ postId, newStatus }: { postId: string; newStatus: PostStatus }) => {
      const response = await postsService.updatePostStatus(clientId, postId, newStatus);
      return PostItem.fromLegacyPost(response.post, UserRole.ADMIN, clientId);
    },
    onMutate: async ({ postId, newStatus }) => {
      await queryClient.cancelQueries({ queryKey: postKeys.all(clientId) });

      const previousPosts = queryClient.getQueryData<PostItem[]>(postKeys.all(clientId));

      if (previousPosts) {
        queryClient.setQueryData<PostItem[]>(postKeys.all(clientId), (old) =>
          old?.map((post) =>
            post.id === postId ? post.withStatus(newStatus as ValidStatuses) : post
          )
        );
      }

      return { previousPosts };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousPosts) {
        queryClient.setQueryData(postKeys.all(clientId), context.previousPosts);
      }
    },
    onSettled: (_data, _error, { postId }) => {
      queryClient.invalidateQueries({ queryKey: postKeys.single(clientId, postId) });
    },
  });
}

/**
 * Mutation for creating a post
 */
export function useCreatePostMutation(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (postData: Parameters<typeof postsService.createOrGetPost>[1]) => {
      const response = await postsService.createOrGetPost(clientId, postData);
      return PostItem.fromLegacyPost(response.post, UserRole.ADMIN, clientId);
    },
    onSuccess: (newPost) => {
      // Add the new post to the beginning of the list
      queryClient.setQueryData<PostItem[]>(postKeys.all(clientId), (old) =>
        old ? [newPost, ...old] : [newPost]
      );
    },
  });
}

/**
 * Mutation for deleting a post
 */
export function useDeletePostMutation(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (postId: string) => {
      await postsService.deletePost(clientId, postId);
      return postId;
    },
    onMutate: async (postId) => {
      await queryClient.cancelQueries({ queryKey: postKeys.all(clientId) });

      const previousPosts = queryClient.getQueryData<PostItem[]>(postKeys.all(clientId));

      // Optimistically remove the post
      if (previousPosts) {
        queryClient.setQueryData<PostItem[]>(postKeys.all(clientId), (old) =>
          old?.filter((post) => post.id !== postId)
        );
      }

      return { previousPosts };
    },
    onError: (_err, _postId, context) => {
      if (context?.previousPosts) {
        queryClient.setQueryData(postKeys.all(clientId), context.previousPosts);
      }
    },
  });
}

/**
 * Utility functions for SSE handlers to update the cache
 * These are used by useRealtimeSync to handle granular updates
 */
export const postCacheUtils = {
  /**
   * Upsert a post directly into the cache (no refetch)
   * Used when we already have the full post data from an API response
   */
  upsertPost(clientId: string, postItem: PostItem): void {
    queryClient.setQueryData<PostItem[]>(postKeys.all(clientId), (old) => {
      if (!old) return [postItem];

      const exists = old.some((p) => p.id === postItem.id);
      if (exists) {
        return old.map((p) => (p.id === postItem.id ? postItem : p));
      } else {
        // New post - add to beginning
        return [postItem, ...old];
      }
    });

    // Also update the single post cache
    queryClient.setQueryData(postKeys.single(clientId, postItem.id), postItem);
  },

  /**
   * Refetch a single post and update it in the list cache
   * Used when SSE notifies us that a post was updated
   */
  async refetchAndUpdatePost(clientId: string, postId: string): Promise<void> {
    try {
      // Fetch the single post
      const post = await postsService.getPost(clientId, postId);
      const postItem = PostItem.fromLegacyPost(post, UserRole.ADMIN, clientId);

      // Update the grid store if this post is displayed on a grid
      useAppStore.getState().updatePostInGrid(post);

      // Update it in the list cache
      queryClient.setQueryData<PostItem[]>(postKeys.all(clientId), (old) => {
        if (!old) return [postItem];

        const exists = old.some((p) => p.id === postId);
        if (exists) {
          return old.map((p) => (p.id === postId ? postItem : p));
        } else {
          // New post - add to beginning
          return [postItem, ...old];
        }
      });

      // Also update the single post cache
      queryClient.setQueryData(postKeys.single(clientId, postId), postItem);
    } catch (error) {
      console.error(`Failed to refetch post ${postId}:`, error);
      // On error, invalidate the whole list to trigger a full refetch
      queryClient.invalidateQueries({ queryKey: postKeys.all(clientId) });
    }
  },

  /**
   * Remove a post from the cache
   * Used when SSE notifies us that a post was deleted
   */
  removePost(clientId: string, postId: string): void {
    queryClient.setQueryData<PostItem[]>(postKeys.all(clientId), (old) =>
      old?.filter((p) => p.id !== postId)
    );
    queryClient.removeQueries({ queryKey: postKeys.single(clientId, postId) });
  },

  /**
   * Invalidate all posts for a client
   * Use sparingly - prefer granular updates
   */
  invalidateAllPosts(clientId: string): void {
    queryClient.invalidateQueries({ queryKey: postKeys.all(clientId) });
  },
};
